import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { usePlatform } from "@/context/PlatformContext";
import { JasrMark } from "@/components/JasrLogo";
import { api } from "@/lib/api";
import { Clock, ArrowRight, BookOpen, Calendar } from "lucide-react";

const CATEGORY_COLORS = {
  "نمو المبيعات":   "#10B981",
  "دليل تقني":      "#3B82F6",
  "إدارة المخزون":  "#F59E0B",
  "تحسين الأداء":   "#8B5CF6",
  "إدارة الأعمال":  "#EF4444",
};

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("ar-SA", { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return iso;
  }
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { name: platformName, logo_base64 } = usePlatform();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true);
    api.get(`/public/blog/${slug}`)
      .then(r => {
        setPost(r.data);
        document.title = `${r.data.title} — ${platformName || "جسر"}`;
      })
      .catch(e => {
        if (e?.response?.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [slug, platformName]);

  const catColor = post ? (CATEGORY_COLORS[post.category] || "#004EEB") : "#004EEB";

  if (notFound) return (
    <div dir="rtl" className="min-h-screen bg-[#F9FAFB] flex flex-col items-center justify-center px-4"
      style={{ fontFamily: "'Tajawal', sans-serif" }}>
      <div className="text-center">
        <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
          <BookOpen className="w-8 h-8 text-slate-400" strokeWidth={1.5} />
        </div>
        <h1 className="text-2xl font-bold text-slate-900 mb-2">المقال غير موجود</h1>
        <p className="text-slate-500 mb-6">لا يمكن العثور على هذا المقال.</p>
        <Link to="/blog" className="px-5 py-2.5 bg-[#004EEB] text-white rounded-xl font-semibold text-sm hover:bg-[#003BBB] transition-colors">
          العودة للمدونة
        </Link>
      </div>
    </div>
  );

  return (
    <div dir="rtl" className="min-h-screen bg-[#F9FAFB]" style={{ fontFamily: "'Tajawal', sans-serif" }}>

      {/* Navbar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            {logo_base64
              ? <img src={logo_base64} alt={platformName} className="w-7 h-7 rounded-lg object-contain" />
              : <JasrMark className="w-7 h-7" />}
            <span className="font-bold text-slate-900">{platformName || "جسر"}</span>
          </Link>
          <Link to="/blog" className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#004EEB] transition-colors">
            <ArrowRight className="w-4 h-4" />
            المدونة
          </Link>
        </div>
      </header>

      {/* Loading skeleton */}
      {loading && (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16 animate-pulse">
          <div className="h-56 bg-slate-200 rounded-2xl mb-8" />
          <div className="h-8 bg-slate-200 rounded w-3/4 mb-4" />
          <div className="space-y-3">
            {[1,2,3,4,5].map(i => <div key={i} className="h-4 bg-slate-100 rounded" />)}
          </div>
        </div>
      )}

      {/* Article */}
      {post && !loading && (
        <>
          {/* Article hero */}
          <div className="relative overflow-hidden"
            style={{ background: `linear-gradient(135deg, ${catColor}15 0%, ${catColor}30 100%)`, minHeight: 220 }}>
            <div className="absolute inset-0"
              style={{
                backgroundImage: `linear-gradient(${catColor}12 1px, transparent 1px), linear-gradient(90deg, ${catColor}12 1px, transparent 1px)`,
                backgroundSize: "32px 32px"
              }} />
            <div className="relative max-w-4xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
              {/* Breadcrumb */}
              <div className="flex items-center gap-2 text-sm text-slate-500 mb-6">
                <Link to="/" className="hover:text-[#004EEB] transition-colors">الرئيسية</Link>
                <span>/</span>
                <Link to="/blog" className="hover:text-[#004EEB] transition-colors">المدونة</Link>
                <span>/</span>
                <span className="text-slate-700 line-clamp-1 max-w-[200px]">{post.title}</span>
              </div>

              {/* Category badge */}
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold mb-4"
                style={{ background: `${catColor}20`, color: catColor, border: `1px solid ${catColor}30` }}>
                {post.category}
              </span>

              <h1 className="text-2xl sm:text-4xl font-bold text-slate-900 leading-snug mb-6 max-w-3xl">
                {post.title}
              </h1>

              {/* Meta */}
              <div className="flex items-center flex-wrap gap-4">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold"
                    style={{ background: post.author_color || "#004EEB" }}>
                    {post.author_initials || "ج"}
                  </div>
                  <span className="font-semibold text-slate-700 text-sm">{post.author}</span>
                </div>
                <div className="flex items-center gap-1.5 text-slate-500 text-sm">
                  <Calendar className="w-4 h-4" />
                  {formatDate(post.published_at)}
                </div>
                <div className="flex items-center gap-1.5 text-slate-500 text-sm">
                  <Clock className="w-4 h-4" />
                  {post.read_time} دقائق للقراءة
                </div>
              </div>
            </div>
          </div>

          {/* Article content */}
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 sm:p-12">
              {/* Excerpt highlighted */}
              <p className="text-lg text-slate-600 leading-relaxed border-r-4 pr-5 mb-8"
                style={{ borderColor: catColor }}>
                {post.excerpt}
              </p>

              {/* Main content */}
              <div
                data-testid="blog-post-content"
                className="prose-blog"
                style={{ fontFamily: "'Tajawal', sans-serif" }}
                dangerouslySetInnerHTML={{ __html: post.content }}
              />
            </div>

            {/* Back to blog */}
            <div className="mt-10 text-center">
              <Link to="/blog"
                className="inline-flex items-center gap-2 px-6 py-3 border border-slate-200 rounded-xl text-slate-600 hover:border-[#004EEB]/30 hover:text-[#004EEB] font-semibold text-sm transition-all hover:-translate-y-0.5">
                <ArrowRight className="w-4 h-4" />
                العودة إلى جميع المقالات
              </Link>
            </div>
          </div>
        </>
      )}

      {/* Footer note */}
      <div className="border-t border-slate-200 py-8 text-center px-4">
        <div className="flex items-center justify-center gap-4 text-sm">
          <Link to="/" className="text-[#004EEB] hover:underline">الرئيسية</Link>
          <span className="text-slate-300">·</span>
          <Link to="/blog" className="text-[#004EEB] hover:underline">المدونة</Link>
          <span className="text-slate-300">·</span>
          <Link to="/contact" className="text-[#004EEB] hover:underline">تواصل معنا</Link>
        </div>
      </div>

      {/* Article content styles */}
      <style>{`
        .prose-blog h2 { font-size: 1.25rem; font-weight: 800; color: #0F172A; margin-top: 2rem; margin-bottom: 0.75rem; }
        .prose-blog p  { font-size: 1rem; line-height: 1.8; color: #475569; margin-bottom: 1rem; }
        .prose-blog ul { list-style: disc; padding-right: 1.5rem; margin-bottom: 1rem; }
        .prose-blog li { font-size: 1rem; line-height: 1.8; color: #475569; margin-bottom: 0.4rem; }
      `}</style>
    </div>
  );
}
